// src/contexts/pessoas/ClienteContext.js
import React, { createContext, useCallback, useContext, useState, useEffect, useRef } from 'react';
import { fetchData, postData, putData, deleteData } from '../../services/Api';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';
import ClienteSearch from '../../pages/pessoas/ClienteSearch';
import ClienteCad from '../../pages/pessoas/ClienteCad.js';
import { Cliente } from '../../models/pessoas/Cliente.js';
import { useSSE } from '../geral/SSEContext.js';
import { corrigirEnderecoCliente, obterClientePorId, obterClientePorTelefone } from '../../services/pessoas/ClienteService';
import { criarCorrecaoEndereco } from '../../utils/CorrecaoEnderecoUtils';
import { enderecoPessoaParaCampos } from '../../utils/enderecoUtils';

const ClienteContext = createContext();

export const useCliente = () => {
    const context = useContext(ClienteContext);
    if (!context) {
        throw new Error('useCliente must be used within a ClienteProvider');
    }
    return context;
};

export const ClienteProvider = ({ children }) => {
    const [clientes, setClientes] = useState([]);
    const { showAlert } = useAlert();
    const { showMessage } = useMessage();
    const [showSearchModalCliente, setShowSearchModalCliente] = useState(false);
    const [showModalCliente, setShowModalCliente] = useState(false);
    const [initialCliente, setInitialCliente] = useState(null); // Store initial client for ClienteCad
    const [novoLimpaCliente, setNovoLimpaCliente] = useState(true);
    const tempOnSelectClienteRef = useRef(null);
    const tempOnSaveClienteRef = useRef(null);
    const ultimoClienteIdRef = useRef(0);
    const { subscribeToEvent } = useSSE();

    const obterMaiorClienteId = useCallback((listaClientes) => (
        (listaClientes || []).reduce((maiorId, cliente) => {
            const clienteId = Number(cliente?.id) || 0;
            return Math.max(maiorId, clienteId);
        }, 0)
    ), []);

    const substituirClientes = useCallback((listaClientes) => {
        const novaLista = Array.isArray(listaClientes) ? listaClientes : [];
        ultimoClienteIdRef.current = obterMaiorClienteId(novaLista);
        setClientes(novaLista);
    }, [obterMaiorClienteId]);

    const mesclarClientes = useCallback((clientesRecebidos) => {
        const novaLista = (Array.isArray(clientesRecebidos) ? clientesRecebidos : [clientesRecebidos])
            .filter(cliente => Number(cliente?.id) > 0);

        if (!novaLista.length) return;

        ultimoClienteIdRef.current = Math.max(
            ultimoClienteIdRef.current,
            obterMaiorClienteId(novaLista)
        );

        setClientes(clientesAtuais => {
            const clientesPorId = new Map(
                clientesAtuais.map(cliente => [cliente.id?.toString(), cliente])
            );

            novaLista.forEach(cliente => {
                clientesPorId.set(cliente.id.toString(), cliente);
            });

            return Array.from(clientesPorId.values());
        });
    }, [obterMaiorClienteId]);

    // Função para abrir o modal e retornar o cliente selecionado
    const showClienteSearch = () => {
        return new Promise((resolve) => {
            setShowSearchModalCliente(true);
            const handleSelect = (clienteSelect) => {
                setShowSearchModalCliente(false);
                resolve(clienteSelect || null);
            };
            tempOnSelectClienteRef.current = handleSelect;
        });
    };

    // Estado temporário para armazenar o callback de seleção

    const handleCloseSearchModal = () => {
        setShowSearchModalCliente(false);
        tempOnSelectClienteRef.current = null;
    };

    // Função para abrir o modal de cadastro com cliente inicial opcional
    const showClienteCad = (initialCliente = new Cliente(), novoLimpa = true) => {
        return new Promise((resolve) => {
            setInitialCliente(initialCliente); // Set the initial client
            setNovoLimpaCliente(novoLimpa);
            setShowModalCliente(true);

            const handleSave = (clienteSalvo) => {
                tempOnSaveClienteRef.current = null;
                resolve(clienteSalvo || null);
            };

            tempOnSaveClienteRef.current = handleSave;
        });
    };

    const handleCloseModalCliente = () => {
        setShowModalCliente(false);
        setInitialCliente(null); // Clear initial client on close
        if (tempOnSaveClienteRef.current) {
            tempOnSaveClienteRef.current(null);
        }
        tempOnSaveClienteRef.current = null;
    };

    useEffect(() => {
        const fetchDataFromAPI = async () => {
            try {
                const result = await fetchData('/Cliente');
                substituirClientes(result);
            } catch (error) {
                console.error("Erro ao buscar clientes:", error);
            }
        };
        fetchDataFromAPI();
    }, [substituirClientes]);

    const buscaClientes = async () => {
        try {
            const result = await fetchData('/Cliente/GetClientePorData');
            substituirClientes(result);
        } catch (error) {
            console.error("Erro ao buscar clientes:", error);
        }
    };

    const addCliente = async (cliente) => {
        try {
            const result = await postData('/Cliente', cliente);
            mesclarClientes(result);
            showAlert('Cliente adicionado com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao adicionar cliente:", error);
            showAlert('Erro ao adicionar cliente!', 'danger');
            showMessage(error?.message || error);
            throw error.data || error;
        }
    };

    const updateCliente = async (cliente) => {
        try {
            const result = await putData('/Cliente', cliente);
            mesclarClientes(result);
            showAlert('Cliente atualizado com sucesso!');
            return result;
        } catch (error) {
            console.error("Erro ao atualizar cliente:", error);
            showAlert('Erro ao atualizar cliente!', 'danger');
            showMessage(error?.message || error);
            throw error.data || error;
        }
    };

    const deleteCliente = async (id) => {
        try {
            await deleteData('/Cliente', { clienteId: id });
            setClientes(clientes.filter(c => c.id !== id));
            showAlert('Cliente excluído com sucesso!');
        } catch (error) {
            console.error("Erro ao excluir cliente:", error);
            showAlert('Erro ao excluir cliente!', 'danger');
            throw error.data || error;
        }
    };

    const atualizarEnderecoCliente = async (clienteId, campos) => {
        const endereco = await corrigirEnderecoCliente(clienteId, campos);
        setClientes(atuais => atuais.map(cliente => Number(cliente.id) === Number(clienteId)
            ? { ...cliente, pessoa: { ...cliente.pessoa, enderecoId: endereco.id,
                endereco: { ...cliente.pessoa?.endereco, ...endereco } } }
            : cliente));
        return endereco;
    };

    const aplicarEnderecoCliente = async ({ clienteId, telefoneCliente, enderecoInicial, enderecoAtual, cidadeId }) => {
        let cliente;
        if (Number(clienteId) > 0) {
            cliente = clientes.find(item => Number(item.id) === Number(clienteId))
                || await obterClientePorId(clienteId);
            if (!cliente?.id) throw new Error('Cliente não encontrado.');
        } else {
            const telefone = (telefoneCliente || '').toString().replace(/\D/g, '');
            if (!telefone) return null;
            cliente = await obterClientePorTelefone(telefone);
            if (!cliente?.id) return null;
        }

        const camposAtuais = enderecoPessoaParaCampos(enderecoAtual);
        const campos = criarCorrecaoEndereco(enderecoInicial, camposAtuais, new Set(Object.keys(camposAtuais)));
        const enderecoCadastrado = enderecoPessoaParaCampos(cliente.pessoa?.endereco);
        const temEnderecoCadastrado = cliente.pessoa?.endereco?.id
            && [enderecoCadastrado.logradouro, enderecoCadastrado.numero, enderecoCadastrado.bairro].some(Boolean);
        if (!temEnderecoCadastrado) {
            // Sem endereço anterior, os campos completos são necessários para criar o cadastro.
            Object.assign(campos, camposAtuais);
            const cidadeEnderecoId = cliente.pessoa?.endereco?.cidadeId || cidadeId;
            if (cidadeEnderecoId) campos.cidadeId = cidadeEnderecoId;
        }

        if (Object.keys(campos).length) {
            const endereco = await atualizarEnderecoCliente(cliente.id, campos);
            cliente = { ...cliente, pessoa: { ...cliente.pessoa, enderecoId: endereco.id,
                endereco: { ...cliente.pessoa?.endereco, ...endereco } } };
        }
        mesclarClientes(cliente);
        return cliente;
    };

    const getClienteByTelefone = async (telefone) => {
        try {
            const telefoneNumerico = (telefone || '').toString().replace(/\D/g, '');
            if (!telefoneNumerico) return null;
            const result = await fetchData('/Cliente/GetClienteByTelefone', { telefone: telefoneNumerico });
            return result || null;
        } catch (error) {
            console.error('Erro ao buscar cliente por telefone:', error);
            return null;
        }
    };

    const getOrCreateClienteByTelefone = async ({ telefone, nome, enderecoCliente }) => {
        const telefoneNumerico = (telefone || '').toString().replace(/\D/g, '');
        if (!telefoneNumerico) return null;

        const enderecoTexto = (enderecoCliente || '').toString().trim();
        const partesEndereco = enderecoTexto
            ? enderecoTexto.split(',').map(p => p.trim()).filter(Boolean)
            : [];

        const limparCampoEndereco = (valor, tipo) => {
            let texto = (valor || '').toString().trim();
            if (!texto) return '';

            if (tipo === 'numero') {
                texto = texto.replace(/^n(?:o|\u00BA|\u00B0)?\s*:?\s*/i, '');
            }
            if (tipo === 'bairro') {
                texto = texto.replace(/^bairro\s*:?\s*/i, '');
            }
            if (tipo === 'complemento') {
                texto = texto.replace(/^complemento\s*:?\s*/i, '');
            }

            return texto.trim();
        };

        const enderecoPayload = partesEndereco.length > 0
            ? {
                logradouro: limparCampoEndereco(partesEndereco[0], 'logradouro'),
                numero: limparCampoEndereco(partesEndereco[1], 'numero'),
                bairro: limparCampoEndereco(partesEndereco[2], 'bairro'),
                complemento: limparCampoEndereco(partesEndereco.slice(3).join(', '), 'complemento'),
            }
            : null;

        const clienteExistente = await getClienteByTelefone(telefoneNumerico);
        if (clienteExistente?.id) {
            const clienteTemEndereco =
                !!clienteExistente?.pessoa?.endereco?.logradouro ||
                !!clienteExistente?.pessoa?.endereco?.bairro ||
                !!clienteExistente?.pessoa?.endereco?.numero ||
                !!clienteExistente?.pessoa?.endereco?.cep;

            if (!clienteTemEndereco && enderecoPayload) {
                try {
                    const clienteAtualizado = {
                        ...clienteExistente,
                        pessoa: {
                            ...clienteExistente.pessoa,
                            endereco: enderecoPayload,
                        },
                    };

                    await updateCliente(clienteAtualizado);
                    return clienteAtualizado;
                } catch (error) {
                    console.error('Erro ao atualizar endereço do cliente:', error);
                    return clienteExistente;
                }
            }

            return clienteExistente;
        }

        const nomeCliente = (nome || '').toString().trim() || `Cliente ${telefoneNumerico}`;

        const clientePayload = {
            pessoa: {
                nome: nomeCliente,
                telefone: { numero: telefoneNumerico },
                ...(enderecoPayload ? { endereco: enderecoPayload } : {}),
            },
        };

        try {
            const novoCliente = await addCliente(clientePayload);
            return novoCliente || null;
        } catch (error) {
            console.error('Erro ao cadastrar cliente por telefone:', error);
            return null;
        }
    };
    const handleSelectCliente = () => {
        setShowSearchModalCliente(false);
    };

    useEffect(() => {
        return subscribeToEvent('cliente-alterado', async (event) => {
            try {
                const evento = JSON.parse(event.data);

                if (evento?.acao === 'criado') {
                    const novosClientes = await fetchData('/Cliente/GetClientesComIdMaiorQue', {
                        ultimoClienteId: ultimoClienteIdRef.current
                    });
                    mesclarClientes(novosClientes);
                    return;
                }

                if (evento?.acao === 'atualizado' && Number(evento?.clienteId) > 0) {
                    const clienteAtualizado = await fetchData('/Cliente/GetClienteById', {
                        clienteId: evento.clienteId
                    });
                    mesclarClientes(clienteAtualizado);
                }
            } catch (error) {
                console.error('Erro ao sincronizar cliente pelo SSE:', error);
            }
        });
    }, [mesclarClientes, subscribeToEvent]);

    const handleSelectClienteSearch = (clienteSelect) => {
        const onSelect = tempOnSelectClienteRef.current || handleSelectCliente;
        onSelect(clienteSelect);
    };

    const handleSaveClienteCad = (clienteSalvo) => {
        tempOnSaveClienteRef.current?.(clienteSalvo);
    };

    return (
        <ClienteContext.Provider value={{ clientes, buscaClientes,
                                        addCliente, updateCliente, deleteCliente, atualizarEnderecoCliente, aplicarEnderecoCliente,
                                        showClienteSearch, showClienteCad, getClienteByTelefone,
                                        getOrCreateClienteByTelefone }}>
            {children}
            <ClienteSearch
                show={showSearchModalCliente}
                onHide={handleCloseSearchModal}
                onSelectCliente={handleSelectClienteSearch}
            />
            <ClienteCad
                show={showModalCliente}
                onHide={handleCloseModalCliente}
                initialCliente={initialCliente} // Pass initialCliente to ClienteCad
                onSaveCliente={handleSaveClienteCad}
                novoLimpa={novoLimpaCliente}
            />
        </ClienteContext.Provider>
    );
};


